import React from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Award, Star, Leaf } from "lucide-react";

import { Button } from "../ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../ui/card";

// Quality tier badge configuration
const QUALITY_BADGES = {
  luxury: { label: "Luxury", className: "bg-amber-100 text-amber-800", icon: Award },
  premium: { label: "Premium", className: "bg-purple-100 text-purple-800", icon: Star },
  "mid-range": { label: "Quality", className: "bg-blue-100 text-blue-800", icon: null },
  budget: { label: "Value", className: "bg-green-100 text-green-800", icon: Leaf },
};

// Component to display a P2P listing
function ListingDisplayCard({ listing }) {
  const navigate = useNavigate();
  if (!listing) return null;

  const {
    title = "Untitled Listing",
    price = "N/A",
    condition = "N/A",
    // Image field may contain: base64 URL, signed URL, or R2 key
    image: imageUrl,
    id = "", // The listing ID
    // Enrichment fields
    quality_tier,
    key_features,
    enrichment_json,
  } = listing;

  // Parse enrichment data if it's a JSON string
  let enrichment = null;
  if (enrichment_json) {
    try {
      enrichment = typeof enrichment_json === 'string'
        ? JSON.parse(enrichment_json)
        : enrichment_json;
    } catch (e) {
      // Ignore parse errors
    }
  }

  // Get quality tier from enrichment or direct field
  const qualityTier = (quality_tier || enrichment?.quality_tier)?.toLowerCase();
  const badge = qualityTier ? QUALITY_BADGES[qualityTier] : null;
  const BadgeIcon = badge?.icon;

  // Get first key feature
  const features = key_features || enrichment?.key_features || [];
  const keyFeature = Array.isArray(features) ? features[0] : null;

  // Check if imageUrl is an R2 key (ends with image extension) vs actual URL
  const isR2Key =
    imageUrl &&
    /\.(jpg|jpeg|png|gif|webp)$/i.test(imageUrl) &&
    !imageUrl.startsWith("data:") &&
    !imageUrl.startsWith("http");

  // Use the image URL from the API, or placeholder if it's an R2 key or missing
  // Note: R2 keys can't be displayed directly in local dev due to bucket mismatch
  // Placeholder dimensions: 300x192 to match h-48 (192px height) with proper aspect ratio
  const displayImageUrl =
    imageUrl && !isR2Key
      ? imageUrl
      : "https://via.placeholder.com/300x192/e5e7eb/6b7280?text=Pre-owned+Item";

  const handleViewDetails = () => {
    if (!id) {
      console.error("Listing ID is missing, cannot navigate to detail page.");
      return;
    }
    console.log("Navigate to detail page for listing:", id);
    navigate(`/p2p/listing/${id}`);
  };

  return (
    <Card className="overflow-hidden border border-neutral-200 hover:border-penta-mint transition-colors duration-200 relative">
      {/* 'Pre-owned' Badge */}
      <div className="absolute top-2 right-2 bg-penta-blue text-white text-xs font-mono uppercase tracking-wide px-2 py-1 z-10">
        Pre-owned
      </div>
      {/* Quality tier badge */}
      {badge && (
        <div className={`absolute top-2 left-2 px-2 py-1 text-xs font-medium rounded flex items-center gap-1 z-10 ${badge.className}`}>
          {BadgeIcon && <BadgeIcon size={12} />}
          {badge.label}
        </div>
      )}
      {/* AI Enhanced indicator */}
      {enrichment && (
        <div className="absolute top-10 right-2 p-1.5 bg-white/90 rounded-full z-10" title="AI Enhanced">
          <Sparkles size={14} className="text-penta-mint" />
        </div>
      )}
      <CardHeader className="p-0">
        <img
          src={displayImageUrl}
          alt={title}
          className="object-cover w-full h-48 bg-penta-grey-light"
        />
      </CardHeader>
      <CardContent className="p-4">
        <CardTitle
          className="text-lg mb-2 truncate text-penta-black"
          title={title}
        >
          {title}
        </CardTitle>
        {/* Key feature if available */}
        {keyFeature && (
          <p className="text-xs text-penta-mint mb-1 truncate">
            {keyFeature}
          </p>
        )}
        <p className="text-sm text-penta-grey-medium mb-1">
          Condition: {condition}
        </p>
        <p className="text-lg font-light text-penta-black">
          ${price ? Number(price).toFixed(2) : "N/A"}
        </p>
      </CardContent>
      <CardFooter className="p-4 pt-0">
        <Button onClick={handleViewDetails} className="w-full">
          View Details
        </Button>
      </CardFooter>
    </Card>
  );
}

export default ListingDisplayCard;
